/**
 * Service Configuration System
 * Manages service metadata and environment-aware URL generation
 */

import { ServiceType } from '../types'

export interface ServiceConfig {
  id: ServiceType
  name: string
  description: string
  shortDescription: string
  pricing: string
  ctaText: string
  icon: string
  features: string[]
  isActive: boolean
  isComingSoon?: boolean
  accentColor: string
  status: 'active' | 'inactive' | 'error'
  // Environment-specific configuration
  subdomain: string
  localPort: number
  packagePath: string
  framework: 'nextjs' | 'vite'
}

export interface EnvironmentConfig {
  isDevelopment: boolean
  isProduction: boolean
  domain: string
  apiUrl: string
}

/**
 * Service configuration with environment-aware URLs
 */
export const serviceConfigs: ServiceConfig[] = [
  {
    id: ServiceType.HR,
    name: 'HR Templates',
    description: 'Custom branded application forms and hiring pages with professional design and seamless integration',
    shortDescription: 'Get Your Branded Hiring Page – From €1,000',
    pricing: 'From €1,000',
    ctaText: 'Coming Soon',
    icon: 'users',
    accentColor: '#10b981',
    features: [
      'Custom branded application forms',
      'Multi-step application process',
      'Mobile-responsive design',
      'Payment integration',
      'Analytics dashboard'
    ],
    isActive: false,
    status: 'inactive',
    subdomain: 'hr',
    localPort: parseInt(import.meta.env?.VITE_HR_TEMPLATES_PORT || (typeof process !== 'undefined' && process.env?.VITE_HR_TEMPLATES_PORT) || '3001'),
    packagePath: 'packages/01_HR_Templates/frontend',
    framework: 'nextjs'
  },
  {
    id: ServiceType.COMPLIANCE,
    name: 'Compliance & Insurance',
    description: 'EU compliance monitoring and product recall insurance with comprehensive legal protection',
    shortDescription: 'Protect Your Business from EU Recalls',
    pricing: 'From €500/month',
    ctaText: 'Coming Soon',
    icon: 'shield-check',
    accentColor: '#f59e0b',
    features: [
      'EU compliance monitoring',
      'Product recall insurance',
      'Legal documentation',
      'Risk assessment',
      'Regulatory updates'
    ],
    isActive: false,
    status: 'inactive',
    subdomain: 'compliance',
    localPort: parseInt(import.meta.env?.VITE_COMPLIANCE_PORT || (typeof process !== 'undefined' && process.env?.VITE_COMPLIANCE_PORT) || '3002'),
    packagePath: 'packages/02_Compliance_Recall_Insurance/frontend',
    framework: 'vite'
  },
  {
    id: ServiceType.RARE_EARTH_PERMIT,
    name: 'Critical Minerals Trading',
    description: 'Professional rare earth and critical mineral trading facilitation with verified Chinese exporters and EU compliance',
    shortDescription: 'Connect with Verified Rare Earth Exporters',
    pricing: 'From $8,000',
    ctaText: 'Request Access',
    icon: 'gem',
    accentColor: '#7c3aed',
    features: [
      'Verified Chinese exporter network',
      'MOFCOM quota facilitation',
      'EU customs compliance',
      'Escrow payment security',
      'Bulk transaction support'
    ],
    isActive: true,
    status: 'active',
    subdomain: 'rare',
    localPort: parseInt(import.meta.env?.VITE_RARE_EARTH_PORT || (typeof process !== 'undefined' && process.env?.VITE_RARE_EARTH_PORT) || '3003'),
    packagePath: 'packages/03_Rare_Earth_Permit/frontend',
    framework: 'vite'
  },
  {
    id: ServiceType.INTELLIGENCE,
    name: 'Business Intelligence',
    description: 'AI-powered job scraping and company hiring insights with advanced analytics and lead generation',
    shortDescription: 'Discover Hiring Companies Instantly',
    pricing: 'From €200/month',
    ctaText: 'Coming Soon',
    icon: 'brain',
    accentColor: '#06b6d4',
    features: [
      'AI-powered job scraping',
      'Company hiring insights',
      'Lead generation tools',
      'Market analysis',
      'Automated reporting'
    ],
    isActive: false,
    status: 'inactive',
    subdomain: 'raid',
    localPort: parseInt(import.meta.env?.VITE_RAID_TO_REVENUE_PORT || (typeof process !== 'undefined' && process.env?.VITE_RAID_TO_REVENUE_PORT) || '3004'),
    packagePath: 'packages/04_Raid_to_Revenue/frontend',
    framework: 'nextjs'
  },
  {
    id: ServiceType.TRADE,
    name: '5G Recommerce',
    description: 'AI-powered 5G technology solutions and hardware sourcing with verified supplier networks',
    shortDescription: 'AI-Powered 5G Technology Solutions',
    pricing: 'From €2,500',
    ctaText: 'Coming Soon',
    icon: 'radio',
    accentColor: '#8b5cf6',
    features: [
      '5G hardware sourcing',
      'AI-powered matching',
      'Verified supplier network',
      'Quality assurance',
      'Global logistics'
    ],
    isActive: false,
    status: 'inactive',
    subdomain: 'recommerce',
    localPort: parseInt(import.meta.env?.VITE_5G_RECOMMERCE_PORT || (typeof process !== 'undefined' && process.env?.VITE_5G_RECOMMERCE_PORT) || '3005'),
    packagePath: 'packages/05_5G_Recommerce/frontend',
    framework: 'vite'
  },
  {
    id: ServiceType.VERPACKG,
    name: 'VerpackG-Löschung',
    description: 'Complete LUCID de-registration and German packaging compliance with legal guarantee',
    shortDescription: 'Eliminate German Packaging Compliance Liability',
    pricing: '€390 one-time',
    ctaText: 'Coming Soon',
    icon: 'document-remove',
    accentColor: '#dc2626',
    features: [
      'Complete LUCID de-registration',
      'Authorized German representation',
      'Legal compliance guarantee',
      'Document preparation',
      'Official submission'
    ],
    isActive: false,
    status: 'inactive',
    subdomain: 'verpackg',
    localPort: parseInt(import.meta.env?.VITE_VERPACKG_PORT || (typeof process !== 'undefined' && process.env?.VITE_VERPACKG_PORT) || '3006'),
    packagePath: 'packages/07_VerpackG_Loschung_Service/frontend',
    framework: 'nextjs'
  }
]

/**
 * Get environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  // Check various ways to determine if we're in development
  const isDevelopment = 
    import.meta.env?.MODE === 'development' ||
    import.meta.env?.VITE_NODE_ENV === 'development' ||
    (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') || 
    (typeof process !== 'undefined' && process.env?.VITE_NODE_ENV === 'development') ||
    (typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ))

  const isProduction = !isDevelopment

  // Get configuration from environment variables with fallbacks
  const domain = import.meta.env?.VITE_DOMAIN || 
                 (typeof process !== 'undefined' && process.env?.VITE_DOMAIN) || 
                 (isProduction ? 'mernaki.de' : 'localhost')

  const apiUrl = import.meta.env?.VITE_API_URL || 
                 (typeof process !== 'undefined' && process.env?.VITE_API_URL) || 
                 (isProduction ? 'https://api.mernaki.de' : 'http://localhost:8000')

  return {
    isDevelopment,
    isProduction,
    domain,
    apiUrl
  }
}

/**
 * Generate environment-aware URL for a service
 */
export function getServiceUrl(serviceConfig: ServiceConfig): string {
  const env = getEnvironmentConfig()
  
  if (env.isDevelopment) {
    return `http://localhost:${serviceConfig.localPort}`
  }
  
  return `https://${serviceConfig.subdomain}.${env.domain}`
}

/**
 * Get service configuration by ID
 */
export function getServiceConfig(serviceId: ServiceType): ServiceConfig | undefined {
  return serviceConfigs.find(config => config.id === serviceId)
}

/**
 * Get all service configurations with environment-aware URLs
 */
export function getAllServiceConfigs(): ServiceConfig[] {
  return serviceConfigs.map(config => ({
    ...config,
    baseUrl: getServiceUrl(config)
  }))
}

/**
 * Get services grouped by status
 */
export function getServicesByStatus() {
  const configs = getAllServiceConfigs()
  
  return {
    active: configs.filter(config => config.status === 'active' && !config.isComingSoon),
    inactive: configs.filter(config => config.status === 'inactive'),
    error: configs.filter(config => config.status === 'error'),
    comingSoon: configs.filter(config => config.isComingSoon)
  }
}

/**
 * Check if we're in development mode
 */
export function isDevelopmentMode(): boolean {
  return getEnvironmentConfig().isDevelopment
}

/**
 * Get the main landing page URL
 */
export function getLandingPageUrl(): string {
  const env = getEnvironmentConfig()
  
  if (env.isDevelopment) {
    return 'http://localhost:3000'
  }
  
  return `https://${env.domain}`
}

/**
 * Get API base URL for the current environment
 */
export function getApiBaseUrl(): string {
  return getEnvironmentConfig().apiUrl
}